import { Order, Product } from "@jasper/shared";
import { HookPlacementBy, stepLabels } from "./workflows.utils";

const updateInformation = async (
  updateOrder: (data: any) => Promise<Order>,
  mappedValues: InformationMappedValues,
  orderId: string,
  product: Product
) => {
  const createOrUpdatePartialDenture = () => {
    const data = {
      missingTeeth: {
        set: mappedValues.teeths.missingTeeth,
      },
    };
    if (!product.removableItem?.length) {
      throw new Error("No removable item found in product");
    }
    const removableItem = product.removableItem[0];
    if (removableItem.partialDenture) {
      const partialDentureId = removableItem.partialDenture.id;
      return {
        update: {
          data: data,
          where: {
            id: {
              equals: partialDentureId,
            },
          },
        },
      };
    }
    return {
      create: data,
    };
  };

  const variables = {
    data: {
      products: {
        update: {
          data: {
            removableItem: {
              update: {
                data: {
                  teethToManufacture: {
                    set: mappedValues.teeths.toExtract,
                  },
                  isReplacement: {
                    set: Boolean(mappedValues.isReplacement),
                  },
                  partialDenture: createOrUpdatePartialDenture(),
                },
                where: {
                  id: product.removableItem[0].id,
                },
              },
            },
          },
          where: {
            id: product.id,
          },
        },
      },
    },
    where: {
      id: orderId,
    },
  };
  try {
    return await updateOrder({ variables: variables });
  } catch (error) {
    console.error("Failed to update partial denture information:", error);
    throw error;
  }
};

const updateProsthesis = async (
  updateOrder: (data: any) => Promise<Order>,
  mappedValues: ProsthesisMappedValues,
  orderId: string,
  product: Product
) => {
  try {
    return await updateOrder({
      variables: {
        data: {
          products: {
            update: {
              data: {
                removableItem: {
                  update: {
                    data: {
                      itemMaterial: {
                        connect: {
                          id: mappedValues.material,
                        },
                      },
                      teethShade: {
                        connect: {
                          id: mappedValues.shade,
                        },
                      },
                      gingivaShade: {
                        connect: {
                          id: mappedValues.gingivaShade,
                        },
                      },
                    },
                    where: {
                      id: product.removableItem[0].id,
                    },
                  },
                },
              },
              where: {
                id: product.id,
              },
            },
          },
        },
        where: {
          id: orderId,
        },
      },
    });
  } catch (error) {
    console.error("Failed to update prosthesis:", error);
    throw error;
  }
};

const updateHooksAndOptions = async (
  updateOrder: (data: any) => Promise<Order>,
  mappedValues: HooksAndOptionsMappedValues,
  orderId: string,
  product: Product
) => {
  try {
    if (!product.removableItem?.[0]?.partialDenture) {
      throw new Error("No partial denture found in product");
    }
    const options = new Set(mappedValues.options || []);
    return await updateOrder({
      variables: {
        data: {
          products: {
            update: {
              data: {
                removableItem: {
                  update: {
                    data: {
                      partialDenture: {
                        update: {
                          data: {
                            hooksTeeth: {
                              set:
                                mappedValues.hookChoice ===
                                HookPlacementBy.DOCTOR
                                  ? mappedValues.hooksPosition
                                  : [],
                            },
                            hooksMaterial: {
                              connect: {
                                id: mappedValues.hooksMaterial,
                              },
                            },
                            palatalMetalBand: {
                              set: options.has("PALATAL_METAL_BAND"),
                            },
                            metalMesh: {
                              set: options.has("METAL_MESH"),
                            },
                            lingualMetalBand: {
                              set: options.has("LINGUAL_METAL_BAND"),
                            },
                          },
                          where: {
                            id: {
                              equals:
                                product.removableItem[0]?.partialDenture?.id,
                            },
                          },
                        },
                      },
                    },
                    where: {
                      id: product.removableItem[0].id,
                    },
                  },
                },
              },
              where: {
                id: product.id,
              },
            },
          },
        },
        where: {
          id: orderId,
        },
      },
    });
  } catch (error) {
    console.error("Failed to update hooks and options:", error);
    throw error;
  }
};

const updateFlow = async (
  updateOrder: (data: any) => Promise<Order>,
  mappedValues: FlowMappedValues,
  orderId: string,
  product: Product
) => {
  try {
    return await updateOrder({
      variables: {
        data: {
          products: {
            update: {
              data: {
                removableItem: {
                  update: {
                    data: {
                      workflowType: {
                        set: mappedValues.workflow,
                      },
                    },
                    where: {
                      id: product.removableItem[0].id,
                    },
                  },
                },
              },
              where: {
                id: product.id,
              },
            },
          },
        },
        where: {
          id: orderId,
        },
      },
    });
  } catch (error) {
    console.error("Failed to update flow:", error);
    throw error;
  }
};

export type InformationMappedValues = Record<string, unknown> & {
  teeths: {
    missingTeeth: number[];
    toExtract: number[];
  };
  isReplacement: boolean;
};

export type ProsthesisMappedValues = Record<string, unknown> & {
  material: string;
  shade: string;
  gingivaShade: string;
};

export type FlowMappedValues = Record<string, unknown> & {
  workflow: string;
};

export type HooksAndOptionsMappedValues = Record<string, unknown> & {
  hookChoice: string;
  hooksPosition?: string[];
  hooksMaterial: string;
  options?: string[];
};

export const savePartialDenture = async (
  updateOrder: (data: { variables: any }) => Promise<Order>,
  mappedValues:
    | InformationMappedValues
    | ProsthesisMappedValues
    | HooksAndOptionsMappedValues
    | FlowMappedValues,
  orderId: string,
  refetchOrder: () => Promise<Order>,
  currentStepLabel: string,
  product: Product
) => {
  try {
    switch (currentStepLabel) {
      case stepLabels.PARTIAL_DENTURE.INFORMATION:
        await updateInformation(
          updateOrder,
          mappedValues as InformationMappedValues,
          orderId,
          product
        );
        await refetchOrder();
        break;
      case stepLabels.PARTIAL_DENTURE.PROTHESIS:
        await updateProsthesis(
          updateOrder,
          mappedValues as ProsthesisMappedValues,
          orderId,
          product
        );
        await refetchOrder();
        break;
      case stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS:
        await updateHooksAndOptions(
          updateOrder,
          mappedValues as HooksAndOptionsMappedValues,
          orderId,
          product
        );
        await refetchOrder();
        break;
      case stepLabels.PARTIAL_DENTURE.FLOW:
        await updateFlow(
          updateOrder,
          mappedValues as FlowMappedValues,
          orderId,
          product
        );
        await refetchOrder();
        break;
      default:
        console.warn(
          `Step "${currentStepLabel}" not handled for partial denture save.`
        );
    }
  } catch (error) {
    console.error("Failed to save partial denture:", error);
    throw error;
  }
};
